import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Button, Input, Tag, Tooltip } from 'antd';
import { toast } from 'react-toastify';
import RuleEditor from './RuleEditor';
import { TextWithChips } from './placeholders';
import outreachRulesApi from '../../api/outreach-rules-api';
import styles from './outreach.module.scss';

/**
 * Editing a rule before it is saved: an AI draft (new rule or a change by description), or a
 * hand edit of an existing rule. Shows what the AI understood, lets the admin fix anything,
 * checks the rule against today's users, then saves it.
 */
// Structural messages (limits, "open only") are not about the text; the assistant skips them.
const STRUCTURAL = /gornju granicu|otvoren samo/i;
const concernsText = (check) =>
  [...(check?.errors || []), ...(check?.warnings || [])].some(
    (m) => !STRUCTURAL.test(m)
  );

const RuleDraft = ({
  catalog,
  draft,
  base,
  onSaved,
  onCancel,
  canReview = true,
}) => {
  const [rule, setRule] = useState(draft.rule);
  const [check, setCheck] = useState(draft.check || null);
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState('');
  const [dirty, setDirty] = useState(false);
  const [review, setReview] = useState(null); // { key, loading, failed, data }
  const [dismissed, setDismissed] = useState(null);
  const [applied, setApplied] = useState(0);
  const reviewKey = useRef(null);
  const g = draft.generation;

  useEffect(() => {
    setRule(draft.rule);
    setCheck(draft.check || null);
    setDirty(false);
    setReview(null);
    setDismissed(null);
    reviewKey.current = null;
  }, [draft]);

  const runReview = async (checked, checkedRule) => {
    if (!canReview || !concernsText(checked)) return;
    const key = JSON.stringify(checkedRule);
    if (reviewKey.current === key) return;
    reviewKey.current = key;
    setReview({ key, loading: true });
    const res = await outreachRulesApi.review(checkedRule);
    if (reviewKey.current !== key) return; // stale answer
    setReview(res.success ? { key, data: res.data } : { key, failed: true });
  };

  const runCheck = async (ruleToCheck = rule) => {
    setChecking(true);
    const res = await outreachRulesApi.preview(ruleToCheck);
    setChecking(false);
    if (!res.success) {
      toast.error(res.message);
      return null;
    }
    setCheck(res.data);
    setDirty(false);
    runReview(res.data, ruleToCheck);
    return res.data;
  };

  const applyFix = (fix) => {
    reviewKey.current = null;
    setReview(null);
    setRule((r) => ({
      ...r,
      instruction: fix.instruction,
      fallback: fix.fallback,
    }));
    setApplied((n) => n + 1);
  };

  // Re-check after the applied fix is committed to state, so it uses the new values.
  useEffect(() => {
    if (applied > 0) runCheck(rule);
  }, [applied]);

  const save = async (status) => {
    const checked = dirty || !check ? await runCheck() : check;
    if (!checked) return;
    if (!checked.valid) {
      toast.error('Pravilo ima greške; ispravite ih pre čuvanja.');
      return;
    }
    setSaving(true);
    const payload = {
      rule,
      ...(g?.generationId ? { generationId: g.generationId } : {}),
      ...(note.trim() ? { note: note.trim() } : {}),
      ...(status ? { status } : {}),
    };
    const res = base
      ? await outreachRulesApi.update(base.id, {
          ...payload,
          expectedVersion: base.version,
        })
      : await outreachRulesApi.create(payload);
    setSaving(false);
    if (!res.success) {
      toast.error(res.message);
      return;
    }
    toast.success(res.message || 'Sačuvano');
    onSaved(res.data);
  };

  const onEdit = (next) => {
    setRule(next);
    setDirty(true);
  };

  const p = check?.preview;
  return (
    <div className={styles.draft}>
      {g && (
        <div className={styles.understood}>
          <div className={styles.understoodHead}>
            <Tag color="green">AI predlog</Tag>
            <span className={styles.prompt}>„{g.prompt}“</span>
          </div>
          {g.interpretation && <p>{g.interpretation}</p>}
          {!g.feasible && (
            <Alert
              type="warning"
              showIcon
              message="Ovo se ne može tačno napraviti od postojećih podataka"
              description={
                g.missing || 'AI je predložio najbliže moguće pravilo.'
              }
            />
          )}
          {g.assumptions?.length > 0 && (
            <div>
              <strong>Pretpostavke koje je AI napravio:</strong>
              <ul>
                {g.assumptions.map((a) => (
                  <li key={a}>{a}</li>
                ))}
              </ul>
            </div>
          )}
          {g.questions?.length > 0 && (
            <Alert
              type="info"
              showIcon
              message="Proverite pre čuvanja"
              description={
                <ul className={styles.tightList}>
                  {g.questions.map((q) => (
                    <li key={q}>{q}</li>
                  ))}
                </ul>
              }
            />
          )}
        </div>
      )}

      <RuleEditor catalog={catalog} value={rule} onChange={onEdit} />

      {check && (
        <div className={styles.checkResult}>
          {check.errors?.length > 0 && (
            <Alert
              type="error"
              showIcon
              message="Greške"
              description={
                <ul className={styles.tightList}>
                  {check.errors.map((e) => (
                    <li key={e}>{e}</li>
                  ))}
                </ul>
              }
            />
          )}
          {check.warnings?.length > 0 && (
            <Alert
              type="warning"
              showIcon
              message="Upozorenja"
              description={
                <ul className={styles.tightList}>
                  {check.warnings.map((w) => (
                    <li key={w}>{w}</li>
                  ))}
                </ul>
              }
            />
          )}
          {canReview &&
            review &&
            dismissed !== review.key &&
            (review.loading || concernsText(check)) && (
              <div className={styles.review}>
                <strong className={styles.reviewTitle}>
                  Predlog asistenta
                </strong>
                {review.loading && (
                  <span className={styles.dim}>
                    Asistent proverava pravilo…
                  </span>
                )}
                {review.failed && (
                  <span className={styles.dim}>
                    Asistent trenutno nije dostupan. Možete nastaviti bez njega.
                  </span>
                )}
                {review.data?.fix && (
                  <div className={styles.reviewFix}>
                    <div>
                      <span className={styles.reviewLabel}>Uputstvo za AI</span>
                      <div className={styles.reviewText}>
                        <TextWithChips
                          catalog={catalog}
                          text={review.data.fix.instruction}
                        />
                      </div>
                    </div>
                    <div>
                      <span className={styles.reviewLabel}>Rezervni tekst</span>
                      <div className={styles.reviewText}>
                        <TextWithChips
                          catalog={catalog}
                          text={review.data.fix.fallback}
                        />
                      </div>
                    </div>
                    {review.data.fix.explanation && (
                      <p className={styles.reviewExplain}>
                        {review.data.fix.explanation}
                      </p>
                    )}
                    <div className={styles.reviewBtns}>
                      <Button
                        type="primary"
                        onClick={() => applyFix(review.data.fix)}
                        loading={checking}
                      >
                        Primeni predlog
                      </Button>
                      <Button onClick={() => setDismissed(review.key)}>
                        Zadrži moje
                      </Button>
                    </div>
                  </div>
                )}
                {review.data?.suggestions?.length > 0 && (
                  <div>
                    <span className={styles.reviewLabel}>Saveti</span>
                    <ul className={styles.tightList}>
                      {review.data.suggestions.map((s) => (
                        <li key={s}>{s}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {review.data?.outOfScope?.length > 0 && (
                  <div>
                    <span className={styles.reviewLabel}>
                      Ovo aplikacija ne može
                    </span>
                    <ul className={styles.tightList}>
                      {review.data.outOfScope.map((s) => (
                        <li key={s}>{s}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          {p && (
            <div className={styles.reach}>
              <span>
                Uslovi važe za <strong>{p.matching}</strong> od {p.evaluated}{' '}
                aktivnih korisnika; poruku bi danas mogli da dobiju{' '}
                <strong>{p.eligible}</strong> (posle opštih ograničenja, bez
                drugih pravila).
              </span>
              {p.samples?.length > 0 && (
                <span className={styles.dim}>
                  Primeri: {p.samples.map((s) => s.name).join(', ')}
                </span>
              )}
            </div>
          )}
          {dirty && (
            <span className={styles.dim}>
              Pravilo je izmenjeno posle provere.
            </span>
          )}
        </div>
      )}

      <div className={styles.actions}>
        <Input
          placeholder="Napomena uz izmenu (nije obavezno)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          className={styles.noteInput}
        />
        <div className={styles.actionBtns}>
          <Tooltip title="Računa koliko korisnika danas ispunjava uslove. Ništa se ne šalje.">
            <Button onClick={() => runCheck()} loading={checking}>
              Proveri
            </Button>
          </Tooltip>
          {base ? (
            <Button type="primary" onClick={() => save()} loading={saving}>
              Sačuvaj izmene
            </Button>
          ) : (
            <>
              <Tooltip title="Pravilo se računa i vidi u probnom radu, ali poruke se ne šalju.">
                <Button onClick={() => save('test')} loading={saving}>
                  Sačuvaj kao test
                </Button>
              </Tooltip>
              <Tooltip title="Poruke se šalju kada je slanje uključeno u Opštim podešavanjima.">
                <Button
                  type="primary"
                  onClick={() => save('active')}
                  loading={saving}
                >
                  Sačuvaj i uključi
                </Button>
              </Tooltip>
            </>
          )}
          <Button type="text" onClick={onCancel}>
            Odbaci
          </Button>
        </div>
      </div>
    </div>
  );
};

RuleDraft.propTypes = {
  catalog: PropTypes.object.isRequired,
  draft: PropTypes.shape({
    rule: PropTypes.object.isRequired,
    check: PropTypes.object,
    generation: PropTypes.object,
  }).isRequired,
  base: PropTypes.shape({ id: PropTypes.string, version: PropTypes.number }),
  onSaved: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  canReview: PropTypes.bool,
};

export default RuleDraft;
